import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    AddUpdateRemoveRegistrationPeriodAttributes,
    LoadRegistrationPeriods,
    LoadRegistrationPeriodsFailure,
    LoadRegistrationPeriodsSuccess,
    LoadRegistrationPeriodsThatUseProduct,
    LoadRegistrationPeriodsThatUseProductSuccess,
    RegistrationPeriodActionFailure,
    ReloadRegistrationPeriod,
    ReloadRegistrationPeriodFailure,
    ReloadRegistrationPeriodSuccess,
    UpsertRegistrationPeriod
} from '../actions/registration-period.action';
import { buildCompanyRegistrationPeriodEntities } from '../entity-builder-utils';
import { CompanyRegistrationPeriodEntity } from '../entity-models';
import { AppState } from '../store';
import { getCurrentLanguageFromLocale } from '../../shared/utils';

export interface RegistrationPeriodState extends EntityState<CompanyRegistrationPeriodEntity> {
    loading: boolean;
    actionLoading: boolean;
    error: any;
    loadedCompanies: string[];

    registrationPeriodIdsByProduct: Record<string, string[]>;
}

function selectRegistrationPeriodId(a: CompanyRegistrationPeriodEntity): string {
    return a.companyRegistrationPeriodId;
}

function sortRegistrationPeriods(a: CompanyRegistrationPeriodEntity, b: CompanyRegistrationPeriodEntity): number {
    return moment(a.effectiveTo).toDate().getTime() - moment(b.effectiveTo).toDate().getTime();
}

export const registrationPeriodAdapter: EntityAdapter<CompanyRegistrationPeriodEntity> =
    createEntityAdapter<CompanyRegistrationPeriodEntity>({
        selectId: selectRegistrationPeriodId,
        sortComparer: sortRegistrationPeriods
    });

export const initialState: RegistrationPeriodState = registrationPeriodAdapter.getInitialState({
    loading: true,
    actionLoading: false,
    error: undefined,
    loadedCompanies: [],
    registrationPeriodIdsByProduct: {}
});

export const registrationPeriodReducer = createReducer(
    initialState,
    on(LoadRegistrationPeriods, (state) => ({ ...state, loading: true })),
    on(LoadRegistrationPeriodsFailure, (state, { error }) => ({ ...state, error, loading: false })),
    on(LoadRegistrationPeriodsSuccess, (state, { companyId, registrationPeriods }) => {
        const language = getCurrentLanguageFromLocale();
        return registrationPeriodAdapter.upsertMany(
            buildCompanyRegistrationPeriodEntities(language?.countryCode, ...(registrationPeriods || [])),
            {
                ...state,
                loading: false,
                loadedCompanies: [...state.loadedCompanies, companyId]
            }
        );
    }),
    on(ReloadRegistrationPeriod, (state) => ({ ...state, actionLoading: true })),
    on(ReloadRegistrationPeriodFailure, (state, { error }) => ({ ...state, error, actionLoading: false })),
    on(ReloadRegistrationPeriodSuccess, (state, { registrationPeriod }) => {
        const language = getCurrentLanguageFromLocale();
        return registrationPeriodAdapter.upsertMany(buildCompanyRegistrationPeriodEntities(language?.countryCode, registrationPeriod), {
            ...state,
            actionLoading: false
        });
    }),
    on(RegistrationPeriodActionFailure, (state, { error }) => ({ ...state, actionLoading: false, error })),

    on(UpsertRegistrationPeriod, (state) => ({ ...state, actionLoading: true })),
    on(AddUpdateRemoveRegistrationPeriodAttributes, (state) => ({ ...state, actionLoading: true })),

    on(LoadRegistrationPeriodsThatUseProduct, (state) => ({ ...state, loading: true })),
    on(LoadRegistrationPeriodsThatUseProductSuccess, (state, { productId, registrationPeriods }) => {
        const language = getCurrentLanguageFromLocale();

        // Update the map
        const registrationPeriodIdsByProduct = { ...state.registrationPeriodIdsByProduct };
        registrationPeriodIdsByProduct[productId] = registrationPeriods.map((period) => period.companyRegistrationPeriodId);

        return registrationPeriodAdapter.upsertMany(
            buildCompanyRegistrationPeriodEntities(language?.countryCode, ...(registrationPeriods || [])),
            {
                ...state,
                loading: false,
                registrationPeriodIdsByProduct
            }
        );
    })
);

const { selectAll } = registrationPeriodAdapter.getSelectors();

export const registrationPeriodState = (state: AppState) => state.registrationPeriodState;
export const allRegistrationPeriods = createSelector(registrationPeriodState, selectAll);
export const registrationPeriodsByCompany = (companyId: string) =>
    createSelector(allRegistrationPeriods, (state) => state.filter((elem) => elem.companyId === companyId));
export const registrationPeriodsLoadedByCompany = (companyId: string) =>
    createSelector(registrationPeriodState, (state) => state.loadedCompanies.includes(companyId));
export const registrationPeriodsLoading = createSelector(registrationPeriodState, (state) => state.loading);
export const registrationPeriodActionLoading = createSelector(registrationPeriodState, (state) => state.actionLoading);
export const registrationPeriodError = createSelector(registrationPeriodState, (state) => state.error);
export const registrationPeriodIdsByProduct = createSelector(registrationPeriodState, (state) => state.registrationPeriodIdsByProduct);
